"use client";

import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  FolderKanban,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DigestBanner } from "@/components/aicheck/DigestBanner";
import { apiUrl } from "@/lib/aicheck/api-base";
import {
  PROJECT_STAGE_LABELS,
  PROJECT_STAGES,
  type ProjectRecord,
  type ProjectStage,
  WATCH_STATE_LABELS,
  type WatchState,
} from "@/lib/aicheck/history/types";
import { projectDetailPath } from "@/lib/aicheck/routes";
import { authService } from "@/services/auth";

const WATCH_STATES: WatchState[] = ["active", "paused", "closed"];

/** ソート可能な列。値は ProjectRecord のフィールドに対応する。 */
type SortKey = "departureDate" | "lastRunAt" | "openActionCount" | "score";
type SortDir = "asc" | "desc";

/** 列ヘッダを初めてクリックしたときのデフォルト方向。 */
const SORT_DEFAULT_DIR: Record<SortKey, SortDir> = {
  departureDate: "asc",
  lastRunAt: "desc",
  openActionCount: "desc",
  score: "desc",
};

const ATTENTION_WINDOW_DAYS = 14;

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** 比較用タイムスタンプへ変換。パース不能・未設定は null（ソート時は常に末尾）。 */
function toTimestamp(value: string | null): number | null {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

/** "YYYY-MM-DD..." 形式の先頭日付部分を、タイムゾーンに依存せず年月日として取り出す。 */
function parseDateOnly(
  value: string,
): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

/** 出発日までの日数（過去なら負値）。出発日不明・パース不能なら null。 */
function getDaysUntilDeparture(departureDate: string | null): number | null {
  if (!departureDate) return null;
  const parsed = parseDateOnly(departureDate);
  if (!parsed) return null;
  const dep = new Date(parsed.y, parsed.m - 1, parsed.d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((dep.getTime() - today.getTime()) / 86_400_000);
}

/** 「出発日が近い（14日以内）かつ未処理アクションあり」= 今日やること。 */
function needsAttention(project: ProjectRecord): boolean {
  if (project.openActionCount <= 0) return false;
  const days = getDaysUntilDeparture(project.departureDate);
  return days != null && days <= ATTENTION_WINDOW_DAYS;
}

/** null は方向に関わらず常に末尾へ送る比較関数。 */
function compareNullable(
  a: number | null,
  b: number | null,
  dir: SortDir,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return dir === "asc" ? a - b : b - a;
}

function StageBadge({ stage }: { stage: ProjectRecord["stage"] }) {
  const colors: Record<string, string> = {
    inquiry: "bg-sky-100 text-sky-700",
    proposal: "bg-indigo-100 text-indigo-700",
    quotation: "bg-violet-100 text-violet-700",
    booking: "bg-amber-100 text-amber-700",
    final: "bg-rose-100 text-rose-700",
    post: "bg-zinc-200 text-zinc-600",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[stage] ?? "bg-zinc-100 text-zinc-600"}`}
    >
      {PROJECT_STAGE_LABELS[stage] ?? stage}
    </span>
  );
}

function ScoreBadge({ score }: { score: number | null }) {
  if (score == null) return <span className="text-zinc-400">—</span>;
  const color =
    score >= 80
      ? "text-emerald-600"
      : score >= 60
        ? "text-amber-600"
        : "text-red-600";
  return <span className={`font-semibold ${color}`}>{score}</span>;
}

/** 出発日バッジ：要対応行にのみ表示し、日数の目安を伝える。 */
function DepartureBadge({ days }: { days: number }) {
  const label =
    days === 0 ? "本日出発" : days > 0 ? `あと${days}日` : `${-days}日超過`;
  return (
    <span className="ml-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">
      {label}
    </span>
  );
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <ArrowUpDown size={12} className="text-zinc-300" />;
  return dir === "asc" ? (
    <ArrowUp size={12} className="text-indigo-600" />
  ) : (
    <ArrowDown size={12} className="text-indigo-600" />
  );
}

function SortableTh({
  label,
  sortKey,
  currentKey,
  currentDir,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  currentDir: SortDir;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
}) {
  const active = currentKey === sortKey;
  return (
    <th
      className={`px-4 py-2.5 font-medium ${align === "right" ? "text-right" : "text-left"}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 ${
          align === "right" ? "flex-row-reverse" : ""
        } ${active ? "text-zinc-900" : "text-zinc-500"} hover:text-zinc-900`}
      >
        {label}
        <SortIcon active={active} dir={currentDir} />
      </button>
    </th>
  );
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState<ProjectRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>("departureDate");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [stageFilter, setStageFilter] = useState<ProjectStage | "all">("all");
  const [watchFilter, setWatchFilter] = useState<WatchState | "all">("all");
  const [attentionOnly, setAttentionOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await authService.getIdToken();
      // 未認証時に送ると別オリジン API の ALB が CORS ヘッダ無しの 401 を返し「CORS error」化する。
      if (!token) throw new Error("未認証です。再度サインインしてください。");
      const res = await fetch(apiUrl("/api/aicheck/projects"), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(data?.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as { projects: ProjectRecord[] };
      setProjects(data.projects ?? []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "案件一覧を読み込めませんでした。",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const attentionCount = useMemo(
    () => projects.filter(needsAttention).length,
    [projects],
  );

  const visibleProjects = useMemo(() => {
    const filtered = projects.filter((project) => {
      if (stageFilter !== "all" && project.stage !== stageFilter) return false;
      if (watchFilter !== "all" && project.watchState !== watchFilter)
        return false;
      if (attentionOnly && !needsAttention(project)) return false;
      return true;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      switch (sortKey) {
        case "departureDate":
          return compareNullable(
            toTimestamp(a.departureDate),
            toTimestamp(b.departureDate),
            sortDir,
          );
        case "lastRunAt":
          return compareNullable(
            toTimestamp(a.lastRunAt),
            toTimestamp(b.lastRunAt),
            sortDir,
          );
        case "openActionCount":
          return compareNullable(a.openActionCount, b.openActionCount, sortDir);
        case "score":
          return compareNullable(
            a.lastOverallScore,
            b.lastOverallScore,
            sortDir,
          );
        default:
          return 0;
      }
    });
    return sorted;
  }, [projects, stageFilter, watchFilter, attentionOnly, sortKey, sortDir]);

  const handleSort = useCallback(
    (key: SortKey) => {
      if (key === sortKey) {
        setSortDir((prevDir) => (prevDir === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir(SORT_DEFAULT_DIR[key]);
      }
    },
    [sortKey],
  );

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderKanban size={22} className="text-zinc-500" />
          <h1 className="text-xl font-semibold text-zinc-900">案件一覧</h1>
          <span className="text-sm text-zinc-500">
            チェック実行履歴のある案件
          </span>
          {attentionCount > 0 && (
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
              要対応 {attentionCount}件
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="flex items-center gap-1.5 rounded border border-zinc-300 px-3 py-1.5 text-sm text-zinc-600 transition-colors hover:bg-zinc-50"
        >
          <RefreshCw size={14} />
          再読み込み
        </button>
      </div>

      <DigestBanner />

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && projects.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select
            value={stageFilter}
            onChange={(e) =>
              setStageFilter(e.target.value as ProjectStage | "all")
            }
            className="h-8 rounded border border-zinc-300 bg-white px-2 text-sm text-zinc-700"
          >
            <option value="all">すべてのステージ</option>
            {PROJECT_STAGES.map((stage) => (
              <option key={stage} value={stage}>
                {PROJECT_STAGE_LABELS[stage]}
              </option>
            ))}
          </select>
          <select
            value={watchFilter}
            onChange={(e) =>
              setWatchFilter(e.target.value as WatchState | "all")
            }
            className="h-8 rounded border border-zinc-300 bg-white px-2 text-sm text-zinc-700"
          >
            <option value="all">すべての状態</option>
            {WATCH_STATES.map((state) => (
              <option key={state} value={state}>
                {WATCH_STATE_LABELS[state]}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setAttentionOnly((v) => !v)}
            className={`h-8 rounded-full border px-3 text-sm font-medium transition-colors ${
              attentionOnly
                ? "border-amber-300 bg-amber-100 text-amber-700"
                : "border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            要対応のみ
          </button>
          {(stageFilter !== "all" ||
            watchFilter !== "all" ||
            attentionOnly) && (
            <button
              type="button"
              onClick={() => {
                setStageFilter("all");
                setWatchFilter("all");
                setAttentionOnly(false);
              }}
              className="h-8 rounded px-2 text-xs text-zinc-500 underline-offset-2 hover:text-zinc-700 hover:underline"
            >
              フィルタ解除
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-zinc-500">
          読み込み中…
        </div>
      ) : projects.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 py-16 text-center text-sm text-zinc-500">
          まだ案件がありません。AI Check を実行すると自動的に登録されます。
        </div>
      ) : visibleProjects.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 py-16 text-center text-sm text-zinc-500">
          条件に一致する案件がありません。
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs text-zinc-500">
                <th className="px-4 py-2.5 font-medium">案件</th>
                <th className="px-4 py-2.5 font-medium">ステージ</th>
                <SortableTh
                  label="出発日"
                  sortKey="departureDate"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTh
                  label="最終チェック"
                  sortKey="lastRunAt"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTh
                  label="スコア"
                  sortKey="score"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                  align="right"
                />
                <th className="px-4 py-2.5 font-medium text-right">指摘</th>
                <SortableTh
                  label="アクション"
                  sortKey="openActionCount"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                  align="right"
                />
                <th className="px-4 py-2.5 font-medium">状態</th>
              </tr>
            </thead>
            <tbody>
              {visibleProjects.map((project) => {
                const attention = needsAttention(project);
                const days = getDaysUntilDeparture(project.departureDate);
                return (
                  <tr
                    key={project.projectKey}
                    className={`border-b border-zinc-100 last:border-b-0 ${
                      attention
                        ? "bg-amber-50/70 hover:bg-amber-50"
                        : "hover:bg-zinc-50"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <Link
                        href={projectDetailPath(project.projectKey)}
                        className="font-medium text-indigo-700 hover:underline"
                      >
                        {project.title || `Project ${project.displayId}`}
                      </Link>
                      <span className="ml-2 text-xs text-zinc-400">
                        {project.externalRef ?? `#${project.displayId}`}
                      </span>
                      {project.sourceKind === "external" && (
                        <span
                          className="ml-2 rounded-full bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700"
                          title={project.sourceLabel ?? "外部データ"}
                        >
                          外部データ
                        </span>
                      )}
                      {project.env && project.env !== "default" && (
                        <span className="ml-2 rounded-full bg-zinc-100 px-1.5 py-0.5 text-[11px] font-medium uppercase text-zinc-500">
                          {project.env}
                        </span>
                      )}
                      {project.healthGist && (
                        <p
                          className="mt-1 flex items-start gap-1 text-xs leading-snug text-zinc-500"
                          title="AIヘルスひとこと（参考）"
                        >
                          <Sparkles
                            size={12}
                            className="mt-0.5 shrink-0 text-indigo-400"
                          />
                          <span>{project.healthGist}</span>
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StageBadge stage={project.stage} />
                    </td>
                    <td className="px-4 py-3 text-zinc-600">
                      {project.departureDate ?? "—"}
                      {attention && days != null && (
                        <DepartureBadge days={days} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-600">
                      {formatDateTime(project.lastRunAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ScoreBadge score={project.lastOverallScore} />
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-600">
                      {project.openFindingCount}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {project.openActionCount > 0 ? (
                        <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700">
                          {project.openActionCount}
                        </span>
                      ) : (
                        <span className="text-zinc-300">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {WATCH_STATE_LABELS[project.watchState] ??
                        project.watchState}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
